import { TamediaStyles } from "@ta-interaktiv/microfrontend-template-helpers";
import { FC, ReactElement, useRef, useState, useEffect } from 'react'
import styled from 'styled-components'
import { useEffectOnce } from 'usehooks-ts'
import { Autocomplete, Card, CardContent, TextField } from '@mui/material'
import { ThemeProvider, createTheme } from '@mui/material/styles'

import Map from 'components/Map'
import ContentGrid from 'components/ContentGrid'
import Column from 'components/Column'
import colorVariables from '../../Globals/colors.module.css'
import fontVariables from '../../Globals/fonts.module.css'

type MapData = {
  NO_COMM: number
  category: string | null
  fireworks: string | null
  link: string
  location: string
  municipality: string
  short_link: string | null
  target_municipality: string | null
  text: string
  time: string
}

const MainComponent: FC<MicroFrontendProps> = ({
  tenant,
  colorMode = 'light',
  properties,
}): ReactElement => {
  const TextRef = useRef<null | HTMLDivElement>(null)
  const globalClasses = [
    colorVariables.globalColors,
    fontVariables.globalFonts,
    colorMode === 'dark' ? colorVariables.globalColorsDark : '',
  ]
  if (properties?.year) {
  }
  let canton: 'GE' | 'VD' = 'GE'
  if (tenant && tenant.key === '24heures') {
    canton = 'VD'
  }

  // canton = 'VD' // for debug
  const [data, setData] = useState<MapData[] | null>(null)
  const [height, setHeight] = useState(0)
  const [municipalities, setMunicipalities] = useState<string[]>([])
  const [municipalityName, setMunicipalityName] = useState<string | null>(null)
  const [municipalityData, setMunicipalityData] = useState<MapData | null>(null)

  const year = properties?.year ? properties.year : '2022'
  let filename =
    canton === 'GE' ? `${year}-data-GE.json` : `${year}-data-VD.json`

  useEffect(() => {
    fetch(
      `https://wch-interactive.storage.googleapis.com/1er-aout/${filename}?nocache=${new Date().getTime()}`
    )
      .then((res) => res.json())
      .then(setData)
  }, [filename])

  useEffectOnce(() => {
    setHeight(window.innerHeight * (canton === 'VD' ? 0.75 : 0.5))
  })

  useEffect(() => {
    if (data && data.length > 0) {
      // Option values
      if (municipalities.length === 0) {
        let allMunicipalities: string[] = []
        data.forEach((item) => allMunicipalities.push(item.municipality))
        setMunicipalities(allMunicipalities)
      }
      // Pick a municipality
      if (!municipalityName) {
        const randomMunicipality = data[Math.floor(Math.random() * data.length)]
        if (randomMunicipality) {
          setMunicipalityName(randomMunicipality.municipality)
          setMunicipalityData(randomMunicipality)
        }
      }
    }
  }, [data, municipalities, municipalityName])

  const scrollToText = () => {
    if (TextRef.current) {
      TextRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }

  const handleInputChange = (event: any) => {
    if (event.key === 'Enter') {
      // Without timeout, previous DOM value gets sent
      setTimeout(() => {
        setMunicipalityName(event.target.value)
        setMunicipalityData(getDataByName(event.target.value))
      }, 50)
      scrollToText()
    }
  }
  const handleChange = (event: any) => {
    if (event.target.getAttribute('role')) {
      setMunicipalityName(event.target.innerText)
      setMunicipalityData(getDataByName(event.target.innerText))
      scrollToText()
    }
  }
  const handleMapEvent = (event: any) => {
    const target_municipality = event.target.getAttribute('data-name')
    if (target_municipality && getDataByName(target_municipality)) {
      setMunicipalityName(target_municipality)
      setMunicipalityData(getDataByName(target_municipality))
      scrollToText()
    }
  }

  const getDataByName = (municipality_name: string) => {
    const result = data?.filter(
      (item) => item.municipality === municipality_name
    )
    if (result?.length === 1) {
      return result[0]
    } else {
      return null
    }
  }

  return (
    <>
      <AppContainer $colorMode={colorMode} className={globalClasses.join(' ')}>
        <ContentGrid>
          <Column className='container'>
            <Section className='white intro-container column'>
              <p>
                <b>
                  Choisir une commune dans le champ de recherche ou sur la carte
                </b>
              </p>
              <ThemeProvider theme={theme}>
                <Autocomplete
                  options={municipalities}
                  disablePortal={true}
                  renderInput={(params) => (
                    <TextField {...params} label='Commune' />
                  )}
                  onChange={handleChange}
                  onInputChange={handleInputChange}
                />
              </ThemeProvider>
            </Section>
          </Column>
          <Column wide>
            <Map
              canton={canton}
              activeRegion={municipalityName}
              height={height}
              colorMode={colorMode}
              handleMapEvent={handleMapEvent}
            />
          </Column>
          <Column>
            <>
              {municipalityData && (
                <Card
                  variant='outlined'
                  className='card'
                  ref={TextRef}
                  sx={{ color: 'inherit' }}
                >
                  <CardContent className='card-content'>
                    <Title>{municipalityData.municipality}</Title>
                    {municipalityData.target_municipality ? (
                      <Info>
                        La fête a lieu à{' '}
                        <b>{municipalityData.target_municipality}</b>:{' '}
                        {municipalityData.location}.
                      </Info>
                    ) : (
                      municipalityData.location && (
                        <Info>{municipalityData.location}</Info>
                      )
                    )}
                    {municipalityData.time && (
                      <Info>{municipalityData.time}</Info>
                    )}
                    <Info>{municipalityData.fireworks}</Info>
                    <Text>{municipalityData.text}</Text>
                    {municipalityData.link && (
                      <Info>
                        <a
                          target='_blank'
                          rel='noreferrer'
                          href={municipalityData.link}
                        >
                          Programme détaillé sur {municipalityData.short_link}
                        </a>
                      </Info>
                    )}
                  </CardContent>
                </Card>
              )}
            </>
          </Column>
        </ContentGrid>
      </AppContainer>
    </>
  )
}
const AppContainer = styled(TamediaStyles)<{
  tenant?: string
  $colorMode: 'light' | 'dark'
}>`
  width: 100%;
  margin: 0 0 3rem 0; // same as ArticleParagraph p
  padding: 0;
  font-family: var(--font-sans-serif);
  color: var(--brandblue-main);
  color: #202346;
  font-size: 1.6em;
  // -webkit-font-smoothing: antialiased;
  // -moz-osx-font-smoothing: grayscale;

  .map-background {
    padding: 10px;
    margin-right: auto;
    width: fit-content;
    border-radius: 0 0 15px 0;
    box-shadow: 0px 0px 20px rgba(0, 0, 0, 0.085);
    position: relative;
    z-index: 1;
    display: flex;
    align-items: left;
    flex-direction: column;
  }
  .intro-container {
    margin-top: 20px; // 20px == left / right gutter margin
    border-radius: 6px 6px 0 0;
    padding: 16px;
  }
  .white {
    background: #ffffff;
  }
  .autocomplete-container {
    border-radius: 0 0 4px 4px;
  }

  .card {
    // width: 100%;
    margin: 1em 0 20px 0; // 20px == left / right gutter margin
  }
  .card-content {
    padding: 16px;
    padding-bottom: 24px;
  }

  a,
  a:visited {
    text-decoration: none;
    background-image: linear-gradient(
      to right,
      rgb(255, 228, 98) 0px,
      rgb(255, 228, 98) 100%
    );
    background-size: 100% 20%;
    background-position-y: 17px;
    background-repeat: repeat-x;
    background-position: bottom;
    color: rgb(32, 35, 70);

    &:hover {
      background-size: 100% 100%;
    }
  }
`
const Section = styled.div`
  margin: 0;
`
const Title = styled.h4`
  font-size: 1.2em;
  margin: 0.8em 0;
`
const Text = styled.p`
  font-family: var(--font-serif);
  line-height: 1.6;
  margin: 2em 0;
  white-space: pre-line;
`
const Info = styled.p`
  color: #444;
  margin: 0.5em 0;
  font-size: 0.8em;
`
const theme = createTheme({
  typography: {
    fontSize: 22,
    fontFamily: 'var(--font-sans-serif)',
  },
})

export default MainComponent
